// src/pages/Leads.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leads as API } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STATUSES = {
  '1': { label: 'Active',        color: 'var(--green)'  },
  '2': { label: 'Follow Up',     color: 'var(--yellow)' },
  '3': { label: 'Closed',        color: 'var(--faint)'  },
  '4': { label: 'Lost',          color: 'var(--red)'    },
  '5': { label: 'Not Interested',color: 'var(--purple)' },
  '6': { label: 'In Progress',   color: 'var(--accent)' },
  '7': { label: 'Quoted',        color: 'var(--accent)' },
  '8': { label: 'Invoiced',      color: 'var(--purple)' },
};
const FILTERS = ['All', 'Active', 'Follow Up', 'Quoted', 'Invoiced', 'Closed', 'Lost'];

export default function Leads() {
  const { user }   = useAuth();
  const navigate   = useNavigate();
  const [leads, setLeads]         = useState([]);
  const [busy, setBusy]           = useState(true);
  const [search, setSearch]       = useState('');
  const [filter, setFilter]       = useState('All');
  const [selected, setSelected]   = useState(null);
  const [history, setHistory]     = useState([]);
  const [histBusy, setHistBusy]   = useState(false);
  const [tab, setTab]             = useState('update'); // 'update' | 'history'
  const [newStat, setNewStat]     = useState('');
  const [reason, setReason]       = useState('');
  const [saving, setSaving]       = useState(false);
  const [statuses, setStatuses]   = useState([]);

  const load = useCallback(() => {
    setBusy(true);
    Promise.all([API.list(), API.statuses()]).then(([leads, stats]) => {
      setLeads(leads);
      setStatuses(stats);
    }).catch(console.error).finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  const loadHistory = (cusID) => {
    setHistBusy(true);
    API.history(cusID).then(setHistory).catch(console.error).finally(() => setHistBusy(false));
  };

  const openModal = (lead) => {
    setSelected(lead);
    setNewStat(lead.lstatus);
    setReason('');
    setTab('update');
    setHistory([]);
  };

  const openHistory = (lead) => {
    setSelected(lead);
    setTab('history');
    loadHistory(lead.id);
  };

  const updateStat = async () => {
    setSaving(true);
    try {
      await API.updateStat({ cusID: selected.id, lstatus: newStat, reason, user_id: user.id });
      setSelected(null);
      load();
    } catch (ex) { alert(ex.message); }
    finally { setSaving(false); }
  };

  const filtered = leads.filter(l => {
    const s = STATUSES[l.lstatus];
    const matchFilter = filter === 'All' || s?.label === filter;
    const matchSearch = !search ||
      (l.company || '').toLowerCase().includes(search.toLowerCase()) ||
      (l.name    || '').toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const countFor = (f) => f === 'All' ? leads.length
    : leads.filter(l => STATUSES[l.lstatus]?.label === f).length;

  return (
    <div className="page">
      <style>{`
        /* ── Mobile layout fixes for Leads ── */
        @media (max-width: 640px) {
          .leads-tabs {
            display: flex;
            flex-wrap: nowrap;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
            gap: .4rem;
            padding-bottom: 4px;
          }
          .leads-tabs::-webkit-scrollbar { display: none; }
          .leads-tabs .tab { flex-shrink: 0; white-space: nowrap; }

          .lead-row {
            flex-direction: column;
            align-items: stretch !important;
            gap: .6rem;
          }
          .lead-row .row-actions {
            flex-wrap: wrap;
            justify-content: flex-start !important;
            gap: .4rem;
          }
          .lead-row .row-actions .btn {
            flex: 1 1 auto;
            min-width: 0;
          }

          .lead-modal-overlay .modal {
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100%;
            border-radius: 0;
            margin: 0;
          }
        }
      `}</style>

      <div className="page-header">
        <div className="page-title">🎯 Lead Manager</div>
        <input className="search-bar" placeholder="Search leads…"
          value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {/* Filter tabs — horizontally scrollable on mobile instead of wrapping/crushing */}
      <div className="tabs leads-tabs">
        {FILTERS.map(f => (
          <button key={f} className={`tab${filter === f ? ' active' : ''}`} onClick={() => setFilter(f)}>
            {f} <span style={{ opacity: .6, fontSize: '.72rem' }}>({countFor(f)})</span>
          </button>
        ))}
      </div>

      {busy ? <div className="spinner-wrap"><div className="spinner" /></div> :
        filtered.length === 0 ? <div className="empty">No leads for this filter</div> :
        filtered.map(l => {
          const s   = STATUSES[l.lstatus] || { label: l.lstatus, color: 'var(--faint)' };
          const age = l.lstat_dt ? Math.floor((Date.now() - new Date(l.lstat_dt)) / 86400000) : null;
          return (
            <div key={l.id} className="list-row lead-row">
              <div className="row-main">
                <div className="row-title">{l.company}</div>
                <div className="row-sub">
                  {l.name}
                  {l.number ? <> · <a href={`tel:${l.number}`} style={{ color: 'var(--accent)' }}>{l.number}</a></> : ''}
                  {age !== null ? <span style={{ color: age > 7 ? 'var(--red)' : 'var(--faint)' }}> · {age}d ago</span> : ''}
                </div>
              </div>
              <div className="row-actions">
                <span className="badge" style={{ background: s.color, color: '#0f172a' }}>{s.label}</span>
                <button className="btn btn-sm btn-ghost" onClick={() => openModal(l)}>Update</button>
                <button className="btn btn-sm btn-ghost" onClick={() => openHistory(l)}>History</button>
                <button className="btn btn-sm btn-outline" onClick={() => navigate(`/quotes/new?cus=${l.id}`)}>Quote</button>
              </div>
            </div>
          );
        })
      }

      {/* Modal */}
      {selected && (
        <div className="modal-overlay lead-modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{selected.company}</span>
              <button className="btn-icon" onClick={() => setSelected(null)}>✕</button>
            </div>

            <div className="tabs" style={{ marginBottom: '1rem' }}>
              <button className={`tab${tab === 'update'  ? ' active' : ''}`} onClick={() => setTab('update')}>Update Status</button>
              <button className={`tab${tab === 'history' ? ' active' : ''}`} onClick={() => { setTab('history'); loadHistory(selected.id); }}>History</button>
            </div>

            {tab === 'update' ? (
              <>
                <label style={{ marginBottom: '1rem' }}>
                  New Status
                  <select value={newStat} onChange={e => setNewStat(e.target.value)}>
                    {Object.entries(STATUSES).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                </label>
                <label style={{ marginBottom: '1.2rem' }}>
                  Notes / Reason
                  <textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Add notes about this status change…" />
                </label>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={updateStat} disabled={saving}>{saving ? 'Saving…' : 'Update'}</button>
                  <button className="btn btn-ghost" onClick={() => setSelected(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <div>
                {histBusy ? <div className="spinner-wrap"><div className="spinner" /></div> :
                  history.length === 0 ? <div className="empty">No history yet</div> :
                  history.map((h, i) => (
                    <div key={i} style={{ display: 'flex', gap: '.8rem', marginBottom: '.8rem', padding: '.6rem', background: 'var(--bg)', borderRadius: 8 }}>
                      <span className="badge" style={{ background: STATUSES[h.status_id]?.color || 'var(--faint)', color: '#0f172a', flexShrink: 0 }}>
                        {STATUSES[h.status_id]?.label || h.status_id}
                      </span>
                      <div>
                        <div style={{ fontSize: '.85rem' }}>{h.reason || '—'}</div>
                        <div style={{ color: 'var(--faint)', fontSize: '.75rem' }}>{h.date}</div>
                      </div>
                    </div>
                  ))
                }
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}